import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const NewtownabbeyChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Management Newtownabbey | Convenient North Belfast Location</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Newtownabbey from our south Belfast clinic on the Ormeau Road. Neuroscience-based approach for stress-related pain, tension headaches, fibromyalgia, IBS pain. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Newtownabbey, stress-related pain Newtownabbey, fibromyalgia Newtownabbey, tension headaches Glengormley Rathcoole" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newtownabbey-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Management Newtownabbey | Convenient North Belfast Location" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Newtownabbey. Neuroscience-based approach for stress-related pain, family stress connection." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Management Newtownabbey",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Newtownabbey from Belfast clinic. Specializing in stress-related pain, tension headaches, fibromyalgia, IBS pain, and parenting stress with NHS-complementary approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/newtownabbey-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Management",
            "areaServed": {
              "@type": "City",
              "name": "Newtownabbey",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can family stress cause chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, chronic stress from parenting pressures, work-life balance struggles, and financial worries directly amplifies pain signals in the nervous system. Research shows clear links between psychological stress and conditions like tension headaches, fibromyalgia, IBS pain, and back pain. When you're constantly stressed about family responsibilities, your nervous system stays in fight-or-flight mode, which turns up the volume on pain signals. Hypnotherapy addresses this stress-pain connection directly."
                }
              },
              {
                "@type": "Question",
                "name": "How convenient is Belfast from Newtownabbey?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Straightforward - our Belfast clinic is at 228 Ormeau Road in south Belfast, reached by taking the M2 (or M5 from Rathcoole and Glengormley) into the city and then heading south along the Ormeau Road. For most Newtownabbey addresses it is a drive of under half an hour outside peak traffic, making specialist pain management easily accessible for busy families. Many clients schedule sessions during school hours or on their way to and from work in Belfast."
                }
              },
              {
                "@type": "Question",
                "name": "Can I attend sessions during school hours or evening?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Clinic days and times vary. Current availability and online booking are on the clinic's own page: https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37 - early evening appointments are available. Online sessions provide complete flexibility for evenings or weekends from your home in Newtownabbey."
                }
              },
              {
                "@type": "Question",
                "name": "What stress-related pain conditions do you treat?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work with Newtownabbey clients experiencing tension headaches and migraines from stress, fibromyalgia exacerbated by family pressures, IBS and abdominal pain (strong gut-brain connection), chronic back and neck pain worsened by stress, TMJ pain from jaw clenching, and general stress-related musculoskeletal pain. The neuroscience-based approach addresses both the pain itself and the stress that amplifies it."
                }
              },
              {
                "@type": "Question",
                "name": "How does parenting stress affect chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Parenting stress keeps your nervous system in constant fight-or-flight mode - juggling childcare, work, finances, and family responsibilities creates chronic stress that amplifies pain signals significantly. Many Newtownabbey parents notice their pain worsens during particularly stressful periods (school holidays, financial pressure, work deadlines). By reducing overall stress levels, improving sleep quality, and teaching your nervous system to calm down, hypnotherapy often produces surprising pain relief that medication alone couldn't achieve."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with IBS pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is particularly effective for IBS pain due to the strong gut-brain connection. Research shows hypnotherapy is one of the most effective treatments for IBS, often more effective than medication. By calming your nervous system, reducing stress and anxiety, and working directly with the gut-brain axis, many IBS sufferers experience significant reductions in pain, bloating, and digestive symptoms within 6-8 sessions."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Newtownabbey
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Stress-related pain hypnotherapy from convenient Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">M2/M5 into Belfast</span> from Newtownabbey • <span className="font-semibold">short drive</span> to the Ormeau Road
                </p>
              </div>
              <p className="text-gray-600">Ultra-convenient access + online sessions available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Newtownabbey
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Newtownabbey from our Belfast clinic at 228 Ormeau Road in south Belfast. The journey is a straightforward run down the M2 into the city and then south along the Ormeau Road (A24), which makes accessing specialist chronic pain hypnotherapy easy for busy Newtownabbey families. With Metro bus services along the Ormeau Road corridor and Botanic and City Hospital stations nearby, the clinic is well connected for pain management support.
              </p>
              <p>
                Newtownabbey's suburban family demographic faces unique chronic pain challenges. The pressures of raising children, maintaining dual-income households, managing work-life balance, and dealing with financial stress create chronic psychological pressure that directly amplifies pain. Many parents push through tension headaches, fibromyalgia, IBS pain, and back pain for years because they simply don't have time to address it - until the pain becomes unbearable.
              </p>
              <p>
                Modern neuroscience shows us that chronic stress doesn't just feel bad psychologically - it physically changes how your nervous system processes pain. When you're constantly stressed about childcare, work deadlines, money, and family responsibilities, your brain stays in fight-or-flight mode. This heightened stress state amplifies pain signals, making manageable discomfort feel unbearable. This is why stress-related pain conditions like tension headaches, fibromyalgia, and IBS are so common among parents and working families.
              </p>
              <p>
                Solution-focused hypnotherapy excels at breaking this stress-pain cycle. By calming your fight-or-flight response, teaching your brain healthier pain processing patterns, improving sleep quality, and addressing the parenting stress and anxiety that amplify pain, many clients experience dramatic reductions in pain levels. With 15 years of nursing experience, I understand pain conditions from a medical perspective and recognize how family stress impacts chronic pain in ways that medication alone cannot address.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stress-Pain Connection */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Stress-Pain Connection
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Family stress and parenting pressures aren't just psychologically difficult - they physically amplify pain in your nervous system
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Parenting Pressure</h3>
                <p className="text-gray-700">
                  Juggling childcare, work, school runs, homework, activities, and family responsibilities creates chronic stress. Your nervous system stays on high alert, amplifying pain signals continuously.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Work-Life Balance</h3>
                <p className="text-gray-700">
                  Trying to maintain careers while parenting, financial pressures of raising children, and lack of personal time create constant background stress that keeps pain signals turned up.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Sleep Deprivation</h3>
                <p className="text-gray-700">
                  Poor sleep from parenting demands or stress intensifies pain perception dramatically. Many parents notice pain worsens significantly when sleep-deprived.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                This is why hypnotherapy is particularly effective for Newtownabbey's family demographic. While it cannot eliminate life stressors (you still have to parent, work, pay bills), it can dramatically reduce how much those stressors amplify your pain. By calming your nervous system, improving sleep quality, reducing anxiety, and teaching your brain to process stress differently, many parents experience surprising pain relief.
              </p>
              <p className="text-gray-700 text-lg">
                The goal isn't to make stress disappear - that's unrealistic. The goal is to help your nervous system handle stress without amplifying pain, allowing you to function better as a parent and partner despite daily pressures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Breaks the Stress-Pain Cycle
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Calm the Stress Response</h3>
                    <p className="text-gray-700">
                      Reduces fight-or-flight activation from parenting stress, work pressure, and financial worries. When stress decreases, pain signals decrease proportionally.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Improve Sleep Quality</h3>
                    <p className="text-gray-700">
                      Poor sleep from stress or parenting demands intensifies pain dramatically. Better sleep through relaxation techniques produces significant pain reduction.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Rewire Pain Processing</h3>
                    <p className="text-gray-700">
                      Uses neuroplasticity to create healthier pain pathways. Your brain has learned to amplify pain during stress - it can learn different responses.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Build Resilience</h3>
                    <p className="text-gray-700">
                      Develop better stress management tools that reduce pain flare-ups during difficult periods. Self-hypnosis techniques you can use at home.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                Research shows hypnotherapy 68% more effective than no treatment for chronic pain. For busy Newtownabbey families, the short journey and flexible timing (daytime sessions during school hours, or online sessions for evenings) makes pain management realistically achievable despite hectic schedules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Stress-Related Pain Conditions We Address
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Tension Headaches & Migraines</h3>
                <p className="text-gray-700">Chronic headaches from stress, poor sleep, jaw clenching. One of the most common stress-related pain conditions in parents.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Fibromyalgia</h3>
                <p className="text-gray-700">Widespread pain and fatigue exacerbated by stress. Family pressures and sleep deprivation significantly worsen fibromyalgia symptoms.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">IBS & Abdominal Pain</h3>
                <p className="text-gray-700">Gut-brain connection means stress directly causes IBS pain. Hypnotherapy is particularly effective for IBS due to this strong connection.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Chronic Back & Neck Pain</h3>
                <p className="text-gray-700">Muscle tension from stress manifests as back and neck pain. Stress reduction often produces dramatic improvements in pain levels.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">TMJ & Jaw Pain</h3>
                <p className="text-gray-700">Jaw clenching from stress creates chronic pain. Many parents don't realize they're clenching due to parenting pressure and work stress.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">General Stress-Related Pain</h3>
                <p className="text-gray-700">Musculoskeletal pain, general body aches, fatigue-related pain. When pain worsens during stressful periods, stress is amplifying it.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ultra-Convenient Access */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Ultra-Convenient for Newtownabbey Families
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                A short drive from Newtownabbey via the M2 or M5, our Belfast clinic on the Ormeau Road is easy to reach. Clinic days and times vary, with early evening appointments available, so many Newtownabbey parents can find a slot that fits around school hours and work - making specialist pain management realistic even with busy family schedules.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">During School Hours</h3>
                  <p className="text-gray-700">Clinic days and times vary, with early evening appointments available, so you can often attend while children are at school. The short journey into south Belfast minimises time away.</p>
                </div>
                <div className="bg-white rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Online Flexibility</h3>
                  <p className="text-gray-700">Online sessions available for evening or weekend timing when clinic hours don't suit. Same professional treatment from your Newtownabbey home.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NHS Complementary */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement. Many chronic pain clinics now recommend hypnotherapy as part of multidisciplinary pain management. By addressing the stress and nervous system components that amplify pain, hypnotherapy enhances medical treatments and helps you regain function.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your GP or pain specialist. I work collaboratively with your healthcare team to provide comprehensive pain management that addresses both physical and psychological factors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Newtownabbey Families Choose Sharon Clare
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Ultra-Convenient Access</h3>
                <p className="text-gray-700">A short drive into south Belfast via the M2 or M5. Easy to fit into busy family schedules during school hours or via online sessions.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Understanding Family Stress</h3>
                <p className="text-gray-700">I understand how parenting pressures, work-life balance struggles, and financial stress amplify pain. The approach addresses real-life family challenges.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">15 Years Nursing Experience</h3>
                <p className="text-gray-700">Medical background brings credibility to pain management. I understand pain conditions and how stress impacts them physiologically.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Evidence-Based Results</h3>
                <p className="text-gray-700">Neuroscience-backed approach with proven effectiveness. Most clients see significant improvements within 6-8 sessions - realistic for family budgets and schedules.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Newtownabbey
            </h2>
            <div className="bg-white rounded-2xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car - Ultra-Convenient</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> M2 into Belfast (M5 from Rathcoole and Glengormley), then south along the Ormeau Road (A24) to number 228
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> A straightforward drive of under half an hour outside peak traffic
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">By public transport:</span> Metro services run along the Ormeau Road corridor, with Botanic and City Hospital the nearest rail stations
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Perfect for Busy Families</h3>
                <p className="text-gray-700">
                  The short journey makes specialist pain management realistic even with hectic family schedules. Many parents schedule sessions during school hours, adding minimal disruption to daily routines.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Newtownabbey Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Chronic tension headaches from stress were destroying me - juggling two kids, full-time work, and financial pressure. The Belfast clinic was so close from Newtownabbey I could go during school hours. Sharon's hypnotherapy addressed the stress-pain connection I didn't even realize existed. My headaches are 80% reduced and I'm functioning so much better."
                </p>
                <p className="font-semibold text-gray-900">M.D., Glengormley - Tension Headaches</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia while raising three kids was impossible. The online sessions meant I could fit treatment into evening time after bedtime. Sharon understood how family stress was amplifying my pain. After 8 sessions my pain levels are manageable and I'm sleeping better. Finally feel like I can be the parent I want to be."
                </p>
                <p className="font-semibold text-gray-900">P.K., Rathcoole - Fibromyalgia</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "IBS pain from stress was affecting my work and family life. The drive from Newtownabbey was literally 10 minutes. Sharon's hypnotherapy worked on the gut-brain connection and my pain has reduced dramatically. I wish I'd done this years ago instead of just taking medication that barely helped."
                </p>
                <p className="font-semibold text-gray-900">T.B., Newtownabbey - IBS Pain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Newtownabbey Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can family stress cause chronic pain?
                </h3>
                <p className="text-gray-700">
                  Yes, chronic stress from parenting pressures, work-life balance struggles, and financial worries directly amplifies pain signals in the nervous system. Research shows clear links between psychological stress and conditions like tension headaches, fibromyalgia, IBS pain, and back pain. When you're constantly stressed about family responsibilities, your nervous system stays in fight-or-flight mode, which turns up the volume on pain signals. Hypnotherapy addresses this stress-pain connection directly, often producing surprising pain relief when stress levels decrease.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How convenient is Belfast from Newtownabbey?
                </h3>
                <p className="text-gray-700">
                  Convenient - our Belfast clinic is at 228 Ormeau Road in south Belfast, reached via the M2 (or M5) into the city and then south along the Ormeau Road. The clinic's location makes specialist pain management easily accessible for busy Newtownabbey families. Clinic days and times vary, with early evening appointments available, so many clients find a slot that fits around school hours without significantly disrupting their daily routines.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I attend sessions during school hours or evening?
                </h3>
                <p className="text-gray-700">
                  Clinic days and times vary, and early evening appointments are available - current availability and online booking are on the{' '}
                  <a
                    href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 font-semibold hover:underline"
                  >
                    clinic's own booking page
                  </a>
                  . Many parents attend during school hours, and the short journey from Newtownabbey means minimal time away. Online sessions also provide complete flexibility for evenings or weekends from your home in Newtownabbey.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What stress-related pain conditions do you treat?
                </h3>
                <p className="text-gray-700">
                  I work with Newtownabbey clients experiencing tension headaches and migraines from family stress, fibromyalgia exacerbated by parenting pressures and sleep deprivation, IBS and abdominal pain (strong gut-brain-stress connection), chronic back and neck pain worsened by muscle tension from stress, TMJ and jaw pain from stress-related clenching, and general stress-related musculoskeletal pain. The neuroscience-based approach addresses both the pain itself and the stress that amplifies it, often producing better results than pain medication alone.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does parenting stress affect chronic pain?
                </h3>
                <p className="text-gray-700">
                  Parenting stress keeps your nervous system in constant fight-or-flight mode - juggling childcare, work, finances, school runs, activities, and family responsibilities creates chronic stress that amplifies pain signals significantly. Many Newtownabbey parents notice their pain worsens during particularly stressful periods like school holidays, work deadlines, or financial pressure. By reducing overall stress levels, improving sleep quality (poor sleep from parenting demands intensifies pain), and teaching your nervous system to calm down, hypnotherapy often produces surprising pain relief that medication alone couldn't achieve.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with IBS pain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is particularly effective for IBS pain due to the strong gut-brain connection. Research shows hypnotherapy is one of the most effective treatments for IBS, often more effective than medication or dietary changes. By calming your nervous system, reducing stress and anxiety, and working directly with the gut-brain axis, many IBS sufferers experience significant reductions in pain, bloating, cramping, and digestive symptoms within 6-8 sessions. The stress reduction component is especially powerful for parents whose IBS worsens during stressful family periods.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Break the Stress-Pain Cycle"
        subtitle="If you're ready to address the family stress that's amplifying your chronic pain, book your initial consultation today and start retraining your nervous system."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default NewtownabbeyChronicPain;
